import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ConfirmDialog } from "./Common";
import { Button } from "./ui/button";

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const summary = (d) => `${d.orgs?.length || 0} organizações, ${d.zones?.length || 0} zonas, ${d.recipes?.length || 0} receitas${d.settings ? " e configurações" : ""}`;

export const ConfigTransfer = ({ onImported }) => {
  const [pending, setPending] = useState(null);
  const fileRef = useRef(null);

  const exportCfg = async () => {
    try {
      const { data } = await api.get("/admin/config/export");
      saveBlob(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }), `dz-configuracao-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success(`Exportado: ${summary(data)}.`);
    } catch (e) {
      toast.error(errMsg(e, "Não foi possível exportar."));
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const doc = JSON.parse(await file.text());
      if (!doc || typeof doc !== "object" || !["orgs", "zones", "recipes", "settings"].some((k) => k in doc)) throw new Error();
      setPending(doc);
    } catch {
      toast.error("Ficheiro inválido: não é uma exportação de configuração (JSON).");
    }
  };

  const doImport = async () => {
    const doc = pending;
    setPending(null);
    try {
      const { data } = await api.post("/admin/config/import", doc);
      const f = (s) => `${s.criadas} criadas / ${s.atualizadas} atualizadas`;
      toast.success(`Importado. Orgs: ${f(data.orgs)}; zonas: ${f(data.zonas)}; receitas: ${f(data.receitas)}.`);
      onImported?.();
    } catch (e) {
      toast.error(errMsg(e, "Não foi possível importar."));
    }
  };

  return (
    <div className="panel fade-up mt-6 max-w-xl p-6" data-testid="config-transfer">
      <div className="eyebrow">Exportar/Importar configuração</div>
      <p className="mt-2 text-sm text-slate-400">
        Guarda uma cópia das organizações, zonas, receitas e configurações num ficheiro JSON. A importação junta por nome: cria o que falta e atualiza o que já existe, <b className="text-slate-200">nunca apaga nada</b>. Pontos, donos das zonas, guerras e temporadas não são incluídos.
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button type="button" onClick={exportCfg} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="config-export-button">
          <Download className="mr-1 h-4 w-4" />Exportar JSON
        </Button>
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="border-slate-700 bg-transparent" data-testid="config-import-button">
          <Upload className="mr-1 h-4 w-4" />Importar JSON
        </Button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFile} data-testid="config-import-input" />
      </div>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} testid="config-import-dialog"
        title="Importar configuração?" confirmLabel="Importar"
        description={pending ? `O ficheiro tem ${summary(pending)}. O que tiver o mesmo nome será atualizado; nada será apagado.` : ""}
        onConfirm={doImport} />
    </div>
  );
};
